import { TransactionDrawer, TransactionItemsList, SubinventoryQtyBox } from '../../../atoms';
import { useAppContext } from '../../../../providers';
import {
  usePlants,
  useInventoryItem,
  useSubinventoryCodes,
  useSubinventoryInfo,
  useTransactionItems,
  useCreateInvMgmtTransaction,
  useUpdateRequest,
} from '../../../../hooks';
import { Select, toast } from '@pella/sash-framework';
import { useState } from 'react';
import * as classes from './IssueToLine.module.css';
import { STATUSES, TRANSACTION_TYPES } from '../../../../data/constants';
import { adjustSubinventoryInfo, formatTransactionsForAPI } from '../../../../data/functions';

/**
 * Drawer for the Issue To Line transaction.
 * Opens when a user clicks Complete on an Accepted request with transactionId === 'ISSUE_TO_LINE'.
 * @param {Local.ParentTransactionDrawerProps} props - Component props
 */
export const IssueToLineDrawer = ({ open, close, request }) => {
  const { selectedPlant } = useAppContext();
  const { mutate: createInvMgmtTransaction, isPending } = useCreateInvMgmtTransaction();
  const [selectedSubinventory, setSelectedSubinventory] = useState('');

  const { mutate: updateRequest } = useUpdateRequest();

  const { transactionItems, addQuantity, addFullLot, deleteItem } = useTransactionItems();

  const { data: plants } = usePlants();

  const plant = plants?.find((p) => p.id === selectedPlant);
  const organizationId = plant?.organizationId ?? 0;

  const inputValue = request?.dynamicInput ?? request?.dropDownListVal1 ?? request?.dropDownListVal2 ?? '';

  const { data: inventoryItem } = useInventoryItem({ item: inputValue, organizationId });

  const inventoryItemId = inventoryItem?.[0]?.inventoryItemId;

  const { data: subinventoryCodes } = useSubinventoryCodes({
    organizationId,
    inventoryItemId: inventoryItemId ?? 0,
  });

  const { data: subinventoryInfo } = useSubinventoryInfo({
    organizationId,
    inventoryItemId: inventoryItemId ?? 0,
    subinventoryCode: selectedSubinventory,
  });

  const uniqueSubinventoryCodes = [...new Set(subinventoryCodes?.map((c) => c.subinventoryCode))];

  const adjustedSubinventoryInfo = adjustSubinventoryInfo(subinventoryInfo || [], transactionItems);

  /** Resets form state and closes the drawer */
  const handleClose = () => {
    close();
  };

  /**
   * Updates the request to Completed status and closes the drawer.
   * @param {(request: Pella.ProductionRequestAPI.Request, onSettled: () => void) => void} completeWithLinkedActionCheck
   * @param {string} errorMessage - Toast message shown if the status update fails.
   */
  const handleCompleteRequest = (completeWithLinkedActionCheck, errorMessage) => {
    completeWithLinkedActionCheck(request, () =>
      updateRequest(
        { id: request.id, status: STATUSES.COMPLETED },
        {
          onSuccess: () => {
            toast.success('Request completed successfully!');
            handleClose();
          },
          onError: () => {
            toast.error(errorMessage);
          },
        },
      ),
    );
  };

  /**
   * Handles form submission
   * @param {(request: Pella.ProductionRequestAPI.Request, onSettled: () => void) => void} completeWithLinkedActionCheck
   */
  const handleSubmit = (completeWithLinkedActionCheck) => {
    const formattedTransactions = formatTransactionsForAPI(transactionItems);

    createInvMgmtTransaction(
      {
        orgId: organizationId,
        transactions: formattedTransactions,
      },
      {
        onSuccess: () =>
          handleCompleteRequest(
            completeWithLinkedActionCheck,
            'Request completed in WMS but failed to update status. Please refresh.',
          ),
        onError: () => toast.error('Failed to create inventory management transaction. Please try again.'),
      },
    );
  };

  const disableComplete = transactionItems.length === 0 || isPending;

  return (
    <TransactionDrawer
      disableSubmit={disableComplete}
      open={open}
      close={handleClose}
      onComplete={handleSubmit}
      request={request}
    >
      {inventoryItem && inventoryItem.length > 0 && (
        <div>
          Selected Org: {organizationId} <br />
          Inventory Item ID: {inventoryItem[0]?.inventoryItemId} <br />
          Requested Item: {inventoryItem[0]?.item} <br />
          Description: {inventoryItem[0]?.description}
        </div>
      )}
      <div>
        <Select
          className={classes.selectItem}
          labelText='Sub Inventory'
          id='SubinventorySelect'
          required
          value={selectedSubinventory}
          onChange={(e) => setSelectedSubinventory(e.target.value)}
          data-testid='subinventory-select'
        >
          <option value='' key=''>
            Select a Subinventory
          </option>
          {uniqueSubinventoryCodes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </Select>
      </div>
      {subinventoryInfo && (
        <SubinventoryQtyBox
          subinventoryQtyInfo={adjustedSubinventoryInfo}
          onAddQuantity={(item, qty, index) =>
            addQuantity(
              item,
              qty,
              index,
              TRANSACTION_TYPES.ISSUE_TO_LINE,
              organizationId,
              request?.action?.dispositionId,
            )
          }
          onAddFullLot={(item, qty, index) =>
            addFullLot(
              item,
              qty,
              index,
              TRANSACTION_TYPES.ISSUE_TO_LINE,
              organizationId,
              request?.action?.dispositionId,
            )
          }
          transactionType={TRANSACTION_TYPES.ISSUE_TO_LINE}
          orgId={organizationId}
        />
      )}
      <TransactionItemsList items={transactionItems} onDelete={deleteItem} />
    </TransactionDrawer>
  );
};
